import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../lib/smoothScroll";
import { Crosshair } from "./Reveal";

const Line = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </div>
);

export const Hero = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-canvas bp-grid pt-[68px]">
      <motion.div style={{ y: gridY }} className="pointer-events-none absolute -right-24 top-24 hidden lg:block font-mono-tech text-[10px] tracking-[0.3em] text-[#9ca3af] rotate-90 origin-top-right">
        MGT-SPEC-2024 / DWG-A1
      </motion.div>

      <div className="mx-auto grid max-w-[1440px] grid-cols-1 lg:grid-cols-12 gap-10 px-5 sm:px-8 pt-14 sm:pt-20 pb-14">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="eyebrow flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-10 bg-red-brand" />
            {t.hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[4.4rem] text-ink"
          >
            <Line delay={0.45}>{t.hero.l1}</Line>
            <Line delay={0.58}>{t.hero.l2}</Line>
            <Line delay={0.71} className="text-red-brand">{t.hero.l3}</Line>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-sm sm:text-base leading-relaxed text-[#525964]"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("solutions")}
              className="chamfer-sm bg-red-brand px-7 py-3.5 font-mono-tech text-xs font-semibold uppercase tracking-[0.18em] text-[#f8f7f4] transition-all duration-200 hover:bg-[#5f1019] hover:-translate-y-0.5"
            >
              {t.hero.cta1}
            </button>
            <button
              data-testid="hero-cta-partner"
              onClick={() => navigate("/partners")}
              className="chamfer-sm border border-[#16181b]/30 px-7 py-3.5 font-mono-tech text-xs font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-200 hover:border-red-brand hover:text-red-brand hover:-translate-y-0.5"
            >
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <Crosshair className="-top-3 -left-2" />
            <Crosshair className="-top-3 -right-2" />
            <Crosshair className="-bottom-3 -left-2" />
            <Crosshair className="-bottom-3 -right-2" />
            <div className="chamfer relative overflow-hidden border border-technical bg-white">
              <motion.img
                style={{ y: imgY, scale: 1.18 }}
                src="/assets/img/boda-hero.jpg"
                alt={t.hero.imgAlt || "Boda boda riders on a dusty Kenyan street — the world MargTor engineers for"}
                className="h-[380px] sm:h-[460px] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#16181b]/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-[#f8f7f4]/90">{t.hero.coords}</span>
                <span
                  data-testid="hero-telemetry-badge"
                  className="flex items-center gap-2 bg-red-brand px-2.5 py-1.5 font-mono-tech text-[10px] font-semibold tracking-[0.2em] text-[#f8f7f4]"
                >
                  <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-[#f8f7f4]" />
                  {t.hero.telemetry}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="border-t border-technical bg-[#f0ede6]/60">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 lg:grid-cols-4 px-5 sm:px-8">
          {t.hero.stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`py-7 pr-6 ${i > 0 ? "lg:border-l border-technical lg:pl-8" : ""} ${i % 2 === 1 ? "border-l lg:border-l pl-6 lg:pl-8" : ""}`}
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink">{s.val}</div>
              <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#525964]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
